import type { SessionEndReason, SessionInfo, SpeechClaimInput, Vote } from '@secretnenshi/shared';
import { getSocket, request, RequestError, type GameSocket } from '../net/socket';
import { forgetSeat, getCurrentSeat, saveSeat, setChosenTheme, setCurrentSeat, findSavedSeat } from './storage';
import { get, initialState, set, type InfoPage, type Notice } from './store';

// ---------------------------------------------------------------------------
// Socket wiring
// ---------------------------------------------------------------------------

export function wireSocket(socket: GameSocket): void {
  socket.on('connect', () => {
    set({ connected: true });
    // Network blip or server restart: take the seat back silently.
    const s = get().session;
    const seat = s ? { code: s.code, name: s.name } : getCurrentSeat();
    if (seat) void resumeSeat(seat.code, seat.name, { quiet: true });
  });
  socket.on('disconnect', () => set({ connected: false }));

  // These can arrive just before the join/rejoin acknowledgement. The server
  // only sends a socket its own seat's game and private state.
  socket.on('game:state', (view) => set({ view, viewReceivedAt: Date.now() }));
  socket.on('role:privateState', (priv) => set({ priv }));

  socket.on('seat:reclaimRequested', (r) =>
    set((s) => ({ reclaimRequests: [...s.reclaimRequests.filter((x) => x.requestId !== r.requestId), r] })),
  );
  socket.on('seat:reclaimClosed', ({ requestId }) =>
    set((s) => ({ reclaimRequests: s.reclaimRequests.filter((x) => x.requestId !== requestId) })),
  );
  socket.on('seat:reclaimed', (session) => adoptSession(session));
  socket.on('seat:reclaimDenied', () => {
    if (get().screen.name === 'reclaim') {
      set({ screen: { name: 'landing' }, notice: { kind: 'error', text: 'The host did not approve the request.' } });
    }
  });

  socket.on('session:ended', ({ reason }) => endSession(reason));
}

const END_MESSAGES: Record<SessionEndReason, string | null> = {
  KICKED: 'The host removed you from the game.',
  SEAT_RECLAIMED: 'Your seat was reclaimed on another device.',
  GAME_DELETED: 'That game no longer exists.',
  LEFT: null,
};

function endSession(reason: SessionEndReason): void {
  const s = get().session;
  if (s) forgetSeat(s.code, s.name);
  setCurrentSeat(null);
  const text = END_MESSAGES[reason];
  set({
    ...initialState,
    themeId: get().themeId,
    connected: get().connected,
    notice: text ? { kind: 'info', text } : null,
  });
}

function adoptSession(session: SessionInfo): void {
  if (session.token) saveSeat(session.code, session.name, session.token);
  setCurrentSeat({ code: session.code, name: session.name });
  set({ session, screen: { name: 'game' }, notice: null, reclaimRequests: [] });
}

// ---------------------------------------------------------------------------
// User actions
// ---------------------------------------------------------------------------

async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
  set({ busy: true, notice: null });
  try {
    return await fn();
  } catch (err) {
    const text = err instanceof RequestError ? err.message : 'Something went wrong.';
    set({ notice: { kind: 'error', text } });
    return undefined;
  } finally {
    set({ busy: false });
  }
}

export function connect(): void {
  const s = getSocket();
  if (!s.connected) s.connect();
}

export function go(screen: ReturnType<typeof get>['screen']): void {
  set({ screen, notice: null });
}

export function showInfo(page: InfoPage): void {
  go({ name: 'info', page });
}

export function showNotice(notice: Notice | null): void {
  set({ notice });
}

export function chooseTheme(themeId: string): void {
  setChosenTheme(themeId);
  set({ themeId });
}

export async function createGame(name: string): Promise<void> {
  const session = await run(() => request('game:create', { name, themeId: get().themeId }));
  if (session) adoptSession(session);
}

/** Join, or quietly rejoin if this browser already holds that seat. */
export async function joinGame(code: string, name: string): Promise<'joined' | 'name-taken' | 'failed'> {
  if (findSavedSeat(code, name) && (await resumeSeat(code, name, { quiet: true }))) return 'joined';

  set({ busy: true, notice: null });
  try {
    adoptSession(await request('game:join', { code, name }));
    return 'joined';
  } catch (err) {
    if (err instanceof RequestError && err.code === 'NAME_TAKEN') return 'name-taken';
    if (err instanceof RequestError && err.code === 'GAME_ALREADY_STARTED') {
      set({ notice: { kind: 'error', text: 'That game has already started. If you were playing, use Reclaim with your name.' } });
      return 'name-taken';
    }
    set({ notice: { kind: 'error', text: err instanceof RequestError ? err.message : 'Something went wrong.' } });
    return 'failed';
  } finally {
    set({ busy: false });
  }
}

/** Spec §60: rejoin with the stored token. Returns false if the seat is gone. */
export async function resumeSeat(code: string, name: string, options: { quiet?: boolean } = {}): Promise<boolean> {
  const saved = findSavedSeat(code, name);
  if (!saved) {
    setCurrentSeat(null);
    return false;
  }
  try {
    const session = await request('game:reconnect', { code, name: saved.name, token: saved.token });
    adoptSession(session);
    return true;
  } catch (err) {
    if (err instanceof RequestError && (err.code === 'BAD_TOKEN' || err.code === 'BAD_REQUEST')) {
      forgetSeat(code, saved.name);
      setCurrentSeat(null);
      if (get().session?.code === code) set({ ...initialState, themeId: get().themeId, connected: get().connected });
    }
    if (!options.quiet) {
      set({ notice: { kind: 'error', text: 'That seat is no longer available on this device.' } });
    }
    return false;
  }
}

export async function requestReclaim(code: string, playerName: string): Promise<void> {
  const res = await run(() => request('seat:reclaimRequest', { code, name: playerName }));
  if (res) set({ screen: { name: 'reclaim', code, playerName, requestId: res.requestId } });
}

export async function decideReclaim(requestId: string, approve: boolean): Promise<void> {
  set((s) => ({ reclaimRequests: s.reclaimRequests.filter((r) => r.requestId !== requestId) }));
  await run(() => request('seat:reclaimDecision', { requestId, approve }));
}

export async function startGame(): Promise<void> {
  await run(() => request('game:start', {}));
}

export async function kickPlayer(playerId: string): Promise<void> {
  await run(() => request('lobby:kick', { playerId }));
}

export async function leaveGame(): Promise<void> {
  const s = get().session;
  const inProgress = get().view?.status === 'ACTIVE';
  await run(() => request('game:leave', {}));
  // Leaving a game in progress keeps the seat for later; otherwise it's gone.
  if (s && !inProgress) forgetSeat(s.code, s.name);
  setCurrentSeat(null);
  set({ ...initialState, themeId: get().themeId, connected: get().connected });
}

// ---------------------------------------------------------------------------
// Gameplay. The server always acts as this socket's own seat.
// ---------------------------------------------------------------------------

export const play = {
  acknowledgeRole: () => run(() => request('role:acknowledge', {})),
  skipReveal: () => run(() => request('role:skipReveal', {})),
  nominate: (premierPlayerId: string) => run(() => request('government:nominatePremier', { premierPlayerId })),
  vote: (vote: Vote) => run(() => request('government:vote', { vote })),
  discard: (cardIndex: number) => run(() => request('policy:ministerDiscard', { cardIndex })),
  enact: (cardIndex: number) => run(() => request('policy:premierEnact', { cardIndex })),
  requestVeto: () => run(() => request('policy:requestVeto', {})),
  vetoDecision: (accept: boolean) => run(() => request('policy:vetoDecision', { accept })),
  selectPlayer: (playerId: string) => run(() => request('power:selectPlayer', { playerId })),
  acknowledgePeek: () => run(() => request('power:acknowledgePeek', {})),
  giveSpeech: (claim: SpeechClaimInput) => run(() => request('speech:give', { claim })),
  voteEndDebate: (end: boolean) => run(() => request('debate:voteEnd', { end })),
  skipRoundPhase: () => run(() => request('round:skip', {})),
  setQuestionPeriod: (questionPeriodSeconds: number) => run(() => request('lobby:settings', { questionPeriodSeconds })),
  pause: () => run(() => request('game:pause', {})),
  resume: () => run(() => request('game:resume', {})),
};
